import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="email-chips"
//
// The share modal takes several recipients, one FileShare row each. Typing an
// address and pressing Enter (or comma, or leaving the field) turns it into a
// chip backed by a hidden recipient_emails[] input, so the form posts an array.
//
// Everything here is a convenience: with JS off the visible email field posts
// on its own and the server creates one share, which is why the field is a real
// form-builder input rather than a decoration.
export default class extends Controller {
  static targets = ["input", "chips", "store", "error"]
  static values = { emails: Array }

  connect() {
    // Seeded on a validation re-render, so one bad address does not cost the
    // member the five good ones they typed before it.
    this.emails = this.hasEmailsValue ? [...this.emailsValue] : []
    this.render()
  }

  keydown(event) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      this.commit()
    } else if (event.key === "Backspace" && this.inputTarget.value === "" && this.emails.length > 0) {
      this.emails.pop()
      this.render()
    }
  }

  // Leaving the field commits what is in it, so a typed address is never lost
  // just because somebody clicked Send without pressing Enter first.
  commit() {
    const raw = this.inputTarget.value.trim()
    if (raw === "") return

    // Split the way the server does, so pasting "a@x.com, b@y.com" becomes two
    // chips rather than one "not a valid email address" that the server then
    // accepts anyway.
    const candidates = raw.split(/[,;\s]+/).filter((value) => value !== "")
    const rejected = []

    candidates.forEach((candidate) => {
      if (!this.valid(candidate)) {
        rejected.push(candidate)
        return
      }
      const email = candidate.toLowerCase()
      if (!this.emails.includes(email)) this.emails.push(email)
    })

    this.inputTarget.value = rejected.join(", ")
    if (rejected.length > 0) {
      this.showError(`${rejected.join(", ")} ${rejected.length === 1 ? "is not a valid" : "are not valid"} email address${rejected.length === 1 ? "" : "es"}.`)
    } else {
      this.clearError()
    }
    this.render()
  }

  remove(event) {
    const index = Number(event.currentTarget.dataset.index)
    this.emails.splice(index, 1)
    this.render()
  }

  // Called on submit so a half-typed address still counts.
  submit() {
    this.commit()
  }

  valid(value) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)
  }

  render() {
    this.chipsTarget.innerHTML = ""
    this.storeTarget.innerHTML = ""

    this.emails.forEach((email, index) => {
      const chip = document.createElement("span")
      chip.className =
        "inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-sm text-gray-700"
      chip.textContent = email

      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "text-gray-400 hover:text-gray-600"
      remove.setAttribute("aria-label", `Remove ${email}`)
      remove.dataset.index = index
      remove.dataset.action = "click->email-chips#remove"
      remove.textContent = "×"
      chip.appendChild(remove)

      this.chipsTarget.appendChild(chip)

      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = "recipient_emails[]"
      hidden.value = email
      this.storeTarget.appendChild(hidden)
    })
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
